import { verifyGoogleCode } from "@/services/common";
import { InfoCircleFilled } from "@ant-design/icons";
import { useIntl, getIntl } from "@umijs/max";
import { Button, Input, Modal, message, ConfigProvider } from "antd";
import { createStyles } from "antd-style";
import React, { useState } from "react";
import { createRoot } from "react-dom/client";

const useStyles = createStyles(({ token }) => ({
  modalTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "16px",
    fontWeight: 500,
    color: token.colorText,
  },
  titleIcon: {
    color: token.colorPrimary,
    fontSize: "20px",
  },
  modalContent: {
    paddingTop: "24px",
  },
  promptText: {
    color: token.colorTextSecondary,
    marginBottom: "16px",
    fontSize: "14px",
  },
  input: {
    height: "40px",
    borderRadius: "4px",
  },
}));

interface WithGoogleProps {
  googleTicket?: string;
}

/**
 * 谷歌验证弹窗组件 (内部使用)
 */
const GoogleVerifyModal: React.FC<{
  onConfirm: (ticket: string) => void;
  onCancel: () => void;
  visible?: boolean;
}> = ({ onConfirm, onCancel, visible = true }) => {
  const { styles } = useStyles();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const intl = getIntl();

  const handleConfirm = async () => {
    if (!code) {
      message.warning(intl.formatMessage({ id: "component.google.required" }));
      return;
    }
    if (code.length !== 6 || !/^\d+$/.test(code)) {
      message.warning(
        intl.formatMessage({ id: "component.google.formatError" }),
      );
      return;
    }

    setLoading(true);
    try {
      const res = await verifyGoogleCode({ googleCode: Number(code) });
      if (res?.verifyToken) {
        onConfirm(res.verifyToken);
      } else {
        message.error(intl.formatMessage({ id: "component.google.noTicket" }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={
        <div className={styles.modalTitle}>
          <InfoCircleFilled className={styles.titleIcon} />
          <span>{intl.formatMessage({ id: "component.google.title" })}</span>
        </div>
      }
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {intl.formatMessage({ id: "pages.common.cancel" })}
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={loading}
          onClick={handleConfirm}
        >
          {intl.formatMessage({ id: "pages.common.ok" })}
        </Button>,
      ]}
      width={440}
      centered
      destroyOnHidden
    >
      <div className={styles.modalContent}>
        <div className={styles.promptText}>
          {intl.formatMessage({ id: "component.google.prompt" })}
        </div>
        <Input
          placeholder={intl.formatMessage({
            id: "component.google.placeholder",
          })}
          value={code}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "");
            setCode(value);
          }}
          onPressEnter={handleConfirm}
          className={styles.input}
          maxLength={6}
          autoFocus
        />
      </div>
    </Modal>
  );
};

/**
 * 直接弹出谷歌验证框并返回票据 (Promise 方式)
 */
const showGoogleVerify = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    const div = document.createElement("div");
    document.body.appendChild(div);
    const root = createRoot(div);

    const destroy = () => {
      root.unmount();
      if (div.parentNode) {
        div.parentNode.removeChild(div);
      }
    };

    const onConfirm = (ticket: string) => {
      resolve(ticket);
      destroy();
    };

    const onCancel = () => {
      reject(new Error("CANCEL"));
      destroy();
    };

    root.render(
      <ConfigProvider>
        <GoogleVerifyModal onConfirm={onConfirm} onCancel={onCancel} />
      </ConfigProvider>,
    );
  });
};

/**
 * 谷歌验证 HOC 组件 / 直接调用函数
 * 1. 作为 HOC 使用: withGoogle(Component)
 * 2. 作为直接调用使用: const ticket = await withGoogle()
 */
export function withGoogle<P extends WithGoogleProps>(
  WrappedComponent?: React.ComponentType<P>,
) {
  // 如果没有传入组件，则作为函数直接调用模式
  if (!WrappedComponent) {
    return showGoogleVerify() as any;
  }

  // 传统的 HOC 模式
  return (
    props: Omit<P, "googleTicket"> & {
      open?: boolean;
      onOpenChange?: (open: boolean) => void;
    },
  ) => {
    const [ticket, setTicket] = useState<string | null>(null);
    const [visible, setVisible] = useState(true);

    React.useEffect(() => {
      if (!props.open) {
        setTicket(null);
        setVisible(true);
      }
    }, [props.open]);

    if (!props.open) {
      return null;
    }

    const handleCancel = () => {
      setVisible(false);
      if (props.onOpenChange) {
        props.onOpenChange(false);
      }
    };

    if (ticket) {
      return <WrappedComponent {...(props as P)} googleTicket={ticket} />;
    }

    return (
      <GoogleVerifyModal
        visible={visible}
        onConfirm={(t) => {
          setTicket(t);
          setVisible(false);
        }}
        onCancel={handleCancel}
      />
    );
  };
}

export default withGoogle;
